import { formatDate } from "@progress/kendo-intl";
import dayjs from "dayjs";
import { ChangeEvent, useEffect, useState, useRef } from "react";
import DatePicker from "react-datepicker";
import { LoadingIndicator } from "../../../core/loading-indicator.component";
import {
  Grid,
  GridColumn,
  GridDataStateChangeEvent,
} from "@progress/kendo-react-grid";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import { getDataWithToken, getEndPoint } from "../../../Utils/network.client";
import { API_END_POINT } from "../../../constants";
import axios from "axios";
import { PDFExport } from "@progress/kendo-react-pdf";
import { useGetAgent } from "../../../apiclient/party/useGetAgentApi";
import AgentMonthlyModel from "../../../models/reports/agentmonthly-report";
import { SelectField } from "../../../core/select-field.component";
import { useGetCompanyDetails } from "../../../apiclient/master/useGetCompanydetailsApi";

interface IAgentMonthlyReport {}

export const AgentMonthlyReport: React.FC = () => {
  const [reportMenu, setReportMenu] = useState<IAgentMonthlyReport[]>([]);
  const [fromDate, setFromDate] = useState<Date>(
    new Date(dayjs().add(0, "months").format("MM/DD/YYYY"))
  );
  const [toDate, setToDate] = useState<Date>(new Date());
  const [AgentMonthList, setAgentMonthlyList] = useState<AgentMonthlyModel[]>(
    []
  );
  const exportExcel = useRef<ExcelExport | null>(null);
  const pdfExportRef = useRef<PDFExport | null>(null);

  const [agent, setAgent] = useState<number>(0);
  const [agentData, setAgentData] = useState<
    { value: string; label: string }[]
  >([]);
  const { data: fetchedAgent } = useGetAgent();
  const [selectedAgentName, setSelectedAgentName] = useState<string>("");

  const initialValues = {
    agentId: "",
    fromDate: fromDate,
  };

  const { data: companyDetails, isLoading: isCompanyLoading } =
    useGetCompanyDetails();
  const companyName = companyDetails?.[0]?.companyName || "Company Name";

  const exportToExcel = () => {
    if (!exportExcel.current) return;

    const options = exportExcel.current.workbookOptions();
    const sheet = options.sheets[0];

    const formattedFromDate = fromDate
      ? new Date(fromDate).toLocaleDateString("en-IN", {
          month: "long",
          year: "numeric",
        })
      : "";

    const headingWithDate = `Milk Acknowledgment Report - ${formattedFromDate}`;

    // Add a single merged heading row with the full string
    sheet.rows.unshift({
      cells: [
        {
          value: headingWithDate,
          colSpan: sheet.columns.length,
          bold: true,
          fontSize: 14,
          textAlign: "center",
        },
      ],
    });
    sheet.rows.unshift({
      cells: [
        {
          value: companyName,
          colSpan: sheet.columns.length,
          bold: true,
          fontSize: 14,
          textAlign: "center",
        },
      ],
    });

    exportExcel.current.save(options);
  };

  const exportToPDF = () => {
    if (pdfExportRef.current) {
      pdfExportRef.current.save();
    }
  };

  useEffect(() => {}, [reportMenu]);

  useEffect(() => {
    if (fetchedAgent) {
      const mappedAgent = fetchedAgent.map((agent: any) => ({
        value: agent.agentId,
        label: agent.agentCode,
      }));
      setAgentData(mappedAgent);
    }
  }, [fetchedAgent]);

  const getAgentMonthList = async () => {
    try {
      const formattedFromDate = dayjs(fromDate).format("MM");
      const formattedFromYear = dayjs(fromDate).format("YYYY");
      const getAgentMonthlyUrl = `${getEndPoint(
        API_END_POINT.REPORT
      )}/api/MonthlyReport/GetAgentMonthlyReport/${agent}/${formattedFromDate}/${formattedFromYear}`;

      const response = await getDataWithToken<any>(getAgentMonthlyUrl);

      // const response = await axios.get(getAgentMonthlyUrl);
      const agentmonthly = response?.data?.result || [];
      setAgentMonthlyList(agentmonthly);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleagentdropdownChange = (
    event: ChangeEvent<HTMLSelectElement>
  ): void => {
    const selectedValue = event.target.value;
    const selectedOption = agentData.find(
      (option) => option.value === selectedValue
    );

    setAgent(Number(selectedValue));
    setSelectedAgentName(selectedOption?.label || "");
  };

  const [dataState, setDataState] = useState<any>({
    skip: 0,
    take: 10,
    // group: [{ field: "mgName" }],  // Default grouping by mgName
  });

  const onDataStateChange = (event: GridDataStateChangeEvent) => {
    setDataState(event.dataState);
  };

  // Calculate total liter
  const calculateTotalLiter = () => {
    return AgentMonthList.reduce((total, item) => total + (item.lit || 0), 0);
  };

  // Calculate total liter value
  const totalLiter = calculateTotalLiter();
  const hasRecords = AgentMonthList.length > 0;

  let grid = (
    <Grid
      data={AgentMonthList}
      {...dataState} // Spread the dataState into the Grid
      groupable={false} // Allows grouping by dragging the column header
      onDataStateChange={onDataStateChange} // Capture any state changes (e.g., grouping)
      sortable={true}
      expandField=""
      columnVirtualization={false}
      resizable={true}
    >
      <GridColumn
        title="S.No"
        cell={(props) => {
          const index = props.dataItem
            ? AgentMonthList.indexOf(props.dataItem) + 1
            : 0;
          return <td>{index}</td>;
        }}
      />
      <GridColumn field="agentCode" title="Agent Code" columnMenu={null} />
      <GridColumn field="agent" title="Agent" columnMenu={null} />
      <GridColumn
        field="productShortName"
        title="Product Short Name"
        columnMenu={null}
      />
      <GridColumn field="ratePerUnit" title="Rate Per Unit" columnMenu={null} />
      <GridColumn width={"170px"} field="pk" title="Packet" columnMenu={null} />
      <GridColumn
        field="lit"
        title="Liter"
        columnMenu={null}
        footerCell={() => (
          <strong>Total Liter: {totalLiter.toFixed(2)}</strong> // Display total liter in footer
        )}
      />
    </Grid>
  );

  return (
    <>
      <div
        className="m-lg-3 mt-lg-1 m-4 card p-3 border-0 m-3 sw-shadow radius-15"
        style={{ minHeight: "500px" }}
      >
        <div className="top-sec-align row">
          <center>
            <h4 className="text-normal title-web mb-0 report-title-color">
              Agent Monthly Report
            </h4>
          </center>
        </div>
        <div className="fade_rule"></div>
        <div className="d-flex justify-content-between mt-4 pt-3 pb-3">
          <div className="d-flex">
            {/* Agent dropdown section with label on top */}
            <div className="me-2 d-flex flex-column">
              <label style={{ marginLeft: "15px" }}>Select Agent</label>
              <select
                value={agent}
                onChange={handleagentdropdownChange}
                className="input"
                style={{ width: "190px", height: "38px" }}
              >
                <option value="">Please Select</option>
                {agentData.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* DatePicker section with label on top */}
            <div className="me-2 d-flex flex-column">
              <label>Select From Month</label>
              <DatePicker
                showMonthYearPicker
                className="form-control ms-auto search-right-align calendarBg"
                selected={fromDate}
                dateFormat="MMMM yyyy" // Displays month as text (e.g., "January 2024")
                onChange={(date: Date) => {
                  setFromDate(date);
                }}
                placeholderText="Select Month & Year"
              />
            </div>

            {/* Search button */}
            <div>
              <label></label>
              <button
                className="btn-primary btn search-btns mt-4"
                onClick={getAgentMonthList}
                type="submit"
              >
                <svg width="17" height="17" viewBox="0 0 17.5 17.5">
                  <path
                    id="Path_magnify"
                    data-name="Path / magnify"
                    d="M9.5,3a6.5,6.5,0,0,1,4.94,10.73l.27.27h.79l5,5L19,20.5l-5-5v-.79l-.27-.27A6.5,6.5,0,1,1,9.5,3m0,2A4.5,4.5,0,1,0,14,9.5,4.481,4.481,0,0,0,9.5,5Z"
                    transform="translate(-3 -3)"
                    fill="#fff"
                  />
                </svg>
              </button>
            </div>

            {/* Export buttons */}
            <div className="d-flex justify-content-end">
              <button
                title="Export Excel"
                className="export-button ms-2"
                onClick={exportToExcel}
                disabled={!hasRecords}
                style={{ marginTop: "23px", marginBottom: "2px" }}
              >
                Export to Excel
              </button>
              <button
                title="Export Excel"
                className="export-button ms-2"
                onClick={exportToPDF}
                disabled={!hasRecords}
                style={{ marginTop: "23px", marginBottom: "2px" }}
              >
                Export to PDF
              </button>
            </div>
          </div>
        </div>
        <div className="row col=12">
          <LoadingIndicator isLoading={false} />
          <PDFExport
            fileName={"Agent_Monthly.pdf"}
            ref={pdfExportRef}
            title="Agent Monthly"
          >
            <div style={{ padding: "10px", backgroundColor: "#fff" }}>
              {/* Company Name */}
              <div style={{ textAlign: "center", marginBottom: "5px" }}>
                <span style={{ fontWeight: "bold", fontSize: "1.5rem" }}>
                  {companyDetails?.[0]?.companyName || "Company Name"}
                </span>
              </div>
              <div style={{ textAlign: "center", marginBottom: "10px" }}>
                <span style={{ fontWeight: "bold", fontSize: "1.25rem" }}>
                  Agent Monthly Report
                  {fromDate && (
                    <>
                      {" - "}
                      {new Date(fromDate).toLocaleDateString("en-IN", {
                        year: "numeric",
                        month: "long",
                      })}
                    </>
                  )}
                  {selectedAgentName ? ` - ${selectedAgentName}` : ""}
                </span>
              </div>
              </div>
              
              {grid}
            <ExcelExport
              fileName={"Agent_Monthly"}
              data={AgentMonthList}
              ref={exportExcel}
            >
              <ExcelExportColumn
                width={150}
                field="agentCode"
                title="Agent Code"
              />
              <ExcelExportColumn width={100} field="agentId" title="Agent Id" />
              <ExcelExportColumn width={100} field="agent" title="Agent" />
              <ExcelExportColumn
                width={150}
                field="productId"
                title="Product Id"
              />
              <ExcelExportColumn
                width={100}
                field="productShortName"
                title="Product Short Name"
              />
              <ExcelExportColumn
                width={100}
                field="ratePerUnit"
                title="Rate Per Unit"
              />
              <ExcelExportColumn width={150} field="pk" title="Packets" />
              <ExcelExportColumn
                width={150}
                field="lit"
                title={`${formatDate(fromDate, "MM-yyyy")} (  )`}
              />
            </ExcelExport>
          </PDFExport>
        </div>
      </div>
    </>
  );
};
